import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { AuthError } from '@/lib/errors.js';

/**
 * Watches the TanStack Query cache for AuthError caused by an expired WorkOS session
 * (getAccessToken returns null after retries and a force-refresh attempt). On detection,
 * redirects silently to /login — the api-auth-provider already attempted a silent token
 * refresh before this fires, so reaching here means the session is truly gone.
 *
 * Must be mounted inside both QueryClientProvider and TanStack Router's router context.
 */
export function useSessionExpiry() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Use refs so the cache subscriber never needs to re-register on pathname changes.
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // Prevent duplicate navigations if multiple queries fail simultaneously.
  const handledRef = useRef(false);

  useEffect(() => {
    return queryClient.getQueryCache().subscribe((event) => {
      if (handledRef.current) return;
      if (event.type !== 'updated') return;
      if (event.action.type !== 'error') return;
      const { error } = event.action;
      if (!(error instanceof AuthError)) return;
      if (error.message !== 'No access token available') return;

      handledRef.current = true;
      const current = pathnameRef.current;
      sessionStorage.setItem('redirectAfterLogin', current);
      navigate({ to: '/login', search: { state: current } as any });
    });
  }, [queryClient, navigate]);
}
